<template>
  <header class="app-header" :class="{ 'app-header--scrolled': isScrolled }">
    <div class="app-header__wrapper">
      <!-- Logo -->
      <div class="app-header__logo">
        <a href="/" class="app-header__logo-link" title="返回首页" @click.prevent="goHome">
          <div class="app-header__logo-icon">
            <img src="../assets/logo/logo.svg" alt="星空 Online" class="app-header__logo-svg" />
          </div>
          <div class="app-header__logo-text">
            <span class="app-header__site-name">星空 Online</span>
            <span class="app-header__site-tagline">织梦者的原创世界</span>
          </div>
        </a>
      </div>

      <!-- 搜索框 -->
      <div class="app-header__search">
        <form @submit.prevent="handleSearch" class="app-header__search-form">
          <i class="fas fa-search app-header__search-icon"></i>
          <input
            type="text"
            v-model="searchTerm"
            placeholder="搜索文章..."
            class="app-header__search-input"
          />
          <kbd class="app-header__search-shortcut">⌘K</kbd>
        </form>
      </div>

      <!-- 桌面端主导航 -->
      <nav class="app-header__nav app-header__nav--desktop">
        <router-link to="/" class="app-header__nav-link" :class="{ active: currentRoute === 'home' }">
          <span class="app-header__nav-icon">🏠</span>
          <span>博客</span>
        </router-link>
        <router-link to="/about" class="app-header__nav-link" :class="{ active: currentRoute === 'about' }">
          <span class="app-header__nav-icon">👤</span>
          <span>关于</span>
        </router-link>
      </nav>

      <!-- 右侧操作区 -->
      <div class="app-header__actions">
        <!-- 移动端搜索按钮 -->
        <button class="app-header__search-toggle" @click="toggleSearch" aria-label="搜索">
          <i class="fas fa-search"></i>
        </button>

        <!-- 主题切换 -->
        <button
          class="app-header__theme-toggle"
          @click="toggleTheme"
          :title="theme === 'dark' ? '切换到亮色模式' : '切换到暗黑模式'"
          aria-label="切换主题"
        >
          <i :class="theme == 'dark' ? 'fas fa-sun' : 'fas fa-moon'"></i>
        </button>

        <!-- 移动端菜单 -->
        <button class="app-header__menu-toggle" @click="toggleMobileMenu" aria-label="菜单">
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </div>

    <!-- 移动端导航 -->
    <transition name="mobile-nav">
      <nav v-if="isMobileMenuOpen" class="app-header__nav app-header__nav--mobile">
        <router-link to="/" class="app-header__nav-link" @click="closeMobileMenu">博客</router-link>
        <router-link to="/about" class="app-header__nav-link" @click="closeMobileMenu">关于</router-link>
      </nav>
    </transition>

    <!-- 全屏搜索面板 -->
    <transition name="search-panel">
      <div v-if="isSearchOpen" class="app-header__search-overlay" @keydown.esc="closeSearch">
        <div class="app-header__search-overlay-bar">
          <button class="app-header__search-overlay-close" @click="closeSearch" aria-label="关闭搜索">
            <i class="fas fa-arrow-left"></i>
          </button>
          <form @submit.prevent="handleSearch" class="app-header__search-overlay-form">
            <div class="app-header__search-overlay-input-wrapper">
              <input
                ref="mobileSearchInput"
                type="text"
                v-model="searchTerm"
                placeholder="搜索文章..."
                class="app-header__search-overlay-input"
                autofocus
              />
              <button
                v-if="searchTerm"
                type="button"
                class="app-header__search-overlay-input-clear"
                @click="searchTerm = ''; mobileSearchInput?.focus()"
                aria-label="清除搜索"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
            <button type="submit" class="app-header__search-overlay-submit" :class="{ active: searchTerm }">搜索</button>
          </form>
        </div>
      </div>
    </transition>
  </header>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useArticleStore } from '../store/article'
import { reportUserSearch, reportUserThemeToggle } from '../utils/metrics'

const router = useRouter()
const route = useRoute()
const store = useArticleStore()

const searchTerm = ref('')
const isMobileMenuOpen = ref(false)
const isSearchOpen = ref(false)
const mobileSearchInput = ref<HTMLInputElement | null>(null)
const isScrolled = ref(false)
const theme = ref<'light' | 'dark'>('light')

const currentRoute = computed(() => route?.name as string | undefined)

const goHome = () => {
  store.markHomeRefresh()
  router.replace({ path: '/', query: {} })
}

const handleSearch = () => {
  if (searchTerm.value.trim()) {
    reportUserSearch(true, searchTerm.value.trim().length)
    router.push({ path: '/search', query: { q: searchTerm.value.trim() } })
  }
}

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false
}

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
}

const toggleSearch = () => {
  isSearchOpen.value = !isSearchOpen.value
  if (isSearchOpen.value) {
    nextTick(() => {
      mobileSearchInput.value?.focus()
    })
  }
}

const closeSearch = () => {
  isSearchOpen.value = false
}

const toggleTheme = () => {
  theme.value = theme.value === 'dark' ? 'light' : 'dark'
  reportUserThemeToggle(theme.value)
  document.documentElement.setAttribute('data-theme', theme.value)
  localStorage.setItem('theme', theme.value)
}

const handleScroll = () => {
  isScrolled.value = window.scrollY > 10
}

const handleKeydown = (e: KeyboardEvent) => {
  if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
    e.preventDefault()
    if (window.innerWidth <= 768) {
      toggleSearch()
    } else {
      document.querySelector('.app-header__search-input')?.focus()
    }
  }
  if (e.key === 'Escape' && isSearchOpen.value) {
    closeSearch()
  }
}

watch(
  () => route.query.q,
  (q) => {
    if (q) {
      searchTerm.value = q as string
    }
  },
  { immediate: true }
)

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true })
  window.addEventListener('keydown', handleKeydown)
  const saved = localStorage.getItem('theme')
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
  theme.value = (saved || (prefersDark ? 'dark' : 'light')) as 'light' | 'dark'
  document.documentElement.setAttribute('data-theme', theme.value)
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
  window.removeEventListener('keydown', handleKeydown)
})
</script>

<style lang="less">
.app-header {
  background: var(--glass-bg-light);
  backdrop-filter: blur(32px) saturate(220%);
  -webkit-backdrop-filter: blur(32px) saturate(220%);
  border-bottom: 1px solid var(--shadow-color);
  position: sticky;
  top: 0;
  z-index: 1000;
  transition: box-shadow 0.3s ease, border-color 0.3s ease;

  &--scrolled {
    background: var(--glass-bg-light-hover);
    box-shadow: 0 4px 24px var(--accent-primary-06), inset 0 1px 0 rgba(255, 255, 255, 0.4), inset 0 -1px 0 var(--shadow-accent-05);
    border-bottom-color: transparent;
  }

  &__wrapper {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 32px;
    display: flex;
    align-items: center;
    gap: 24px;
    height: 72px;
  }

  &__logo {
    flex-shrink: 0;

    &-link {
      display: flex;
      align-items: center;
      gap: 12px;
      text-decoration: none;
      transition: transform 0.3s ease;

      &:hover {
        transform: scale(1.02);
      }
    }

    &-icon {
      width: 40px;
      height: 40px;
      background: transparent;
      border-radius: 20px;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 12px var(--shadow-accent-30);
      transition: transform 0.3s ease, box-shadow 0.3s ease;

      .app-header--scrolled & {
        transform: scale(0.95);
      }

      &:hover {
        transform: scale(1.1) rotate(5deg);
        box-shadow: 0 6px 20px var(--shadow-accent-40);
      }
    }

    &-svg {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: inherit;
    }

    &-text {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

  }

  &__site-name {
    font-size: 18px;
    font-weight: 700;
    background: var(--primary-gradient);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    letter-spacing: 0.5px;
  }

  &__site-tagline {
    font-size: 12px;
    color: #999;
    font-weight: 400;
  }

  &__search {
    flex: 1; margin: 0 auto;
    max-width: 800px;

    &-form {
      position: relative;
      display: flex;
      align-items: center;
    }

    &-input {
      width: 100%;
      height: 44px;
      padding: 0 48px 0 44px;
      border: 1.5px solid rgba(255, 255, 255, 0.3);
      border-radius: 22px;
      background: var(--bg-primary);
      font-size: 14px;
      color: #333;
      transition: all 0.3s ease;

      &::placeholder {
        color: #aaa;
      }

      &:focus {
        border-color: var(--shadow-accent-50);
        background: var(--bg-secondary);
        box-shadow:
          0 0 20px var(--accent-primary-15),
          0 0 40px var(--shadow-color),
          0 0 0 3px var(--accent-primary-10);
        outline: none;
      }
    }

    &-icon {
      position: absolute;
      left: 16px;
      color: #999;
      font-size: 16px;
      transition: color 0.3s ease;

      .app-header__search-input:focus + & {
        color: var(--accent-primary);
      }
    }

    &-shortcut {
      position: absolute;
      right: 12px;
      padding: 4px 8px;
      background: rgba(0, 0, 0, 0.06);
      border: 1px solid rgba(0, 0, 0, 0.1);
      border-radius: 6px;
      font-size: 12px;
      color: rgba(0, 0, 0, 0.5);
      font-family: inherit;
      pointer-events: none;
    }
  }

  &__nav {
    &--desktop {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-left: auto;
    }

    &-link {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 10px 16px;
      color: #666;
      text-decoration: none;
      font-size: 14px;
      font-weight: 500;
      border-radius: 20px;
      transition: all 0.3s ease;
      position: relative;

      &::after {
        content: '';
        position: absolute;
        bottom: 6px;
        left: 16px;
        right: 16px;
        height: 2px;
        background: var(--primary-gradient);
        border-radius: 1px;
        transform: scaleX(0);
        transition: transform 0.3s ease;
      }

      &:hover {
        color: var(--accent-primary);
        background: var(--accent-primary-06);
      }

      &.active {
        color: var(--accent-primary);
        background: rgba(255, 255, 255, 0.15);

        &::after {
          transform: scaleX(1);
        }
      }
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: 16px;
  }

  &__theme-toggle {
    width: 44px;
    height: 44px;
    border: none;
    border-radius: 12px;
    background: linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

    &:hover {
      transform: scale(1.05) rotate(5deg);
      box-shadow: 0 4px 16px var(--shadow-accent-20);
    }

    &-inner {
      position: relative;
      width: 20px;
      height: 20px;
    }

    &-icon--light,
    &-icon--dark {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      transition: all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
      font-size: 18px;
    }

    &-icon--light {
      color: #f59e0b;
      opacity: 0;
      transform: translate(-50%, -50%) rotate(-90deg) scale(0);
    }

    &-icon--dark {
      color: #4f46e5;
      opacity: 1;
      transform: translate(-50%, -50%) rotate(0) scale(1);
    }

    [data-theme="dark"] & {
      background: linear-gradient(135deg, #2d3748 0%, #1a202c 100%);

      .app-header__theme-icon--light {
        opacity: 1;
        transform: translate(-50%, -50%) rotate(0) scale(1);
      }

      .app-header__theme-icon--dark {
        opacity: 0;
        transform: translate(-50%, -50%) rotate(90deg) scale(0);
      }
    }
  }

  &__menu-toggle {
    display: none;
    flex-direction: column;
    gap: 5px;
    width: 40px;
    height: 40px;
    border: none;
    border-radius: 20px;
    background: linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%);
    cursor: pointer;
    justify-content: center;
    align-items: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    transition: all 0.3s ease;

    span {
      display: block;
      height: 2px;
      width: 20px;
      background: #666;
      border-radius: 1px;
      transition: all 0.3s ease;
    }

    &:hover span {
      background: var(--accent-primary);
    }

    &[aria-expanded="true"] {
      span:nth-child(1) {
        transform: translateY(7px) rotate(45deg);
      }
      span:nth-child(2) {
        opacity: 0;
      }
      span:nth-child(3) {
        transform: translateY(-7px) rotate(-45deg);
      }
    }
  }
}

// 暗黑模式
[data-theme="dark"] .app-header {
  background: var(--glass-bg-dark);
  backdrop-filter: blur(32px) saturate(160%);
  -webkit-backdrop-filter: blur(32px) saturate(160%);
  border-bottom-color: rgba(255, 255, 255, 0.08);
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05);

  &--scrolled {
    background: var(--glass-bg-dark-hover);
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.08);
  }

  .app-header__site-name {
    background: var(--primary-gradient);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .app-header__site-tagline {
    color: #777;
  }

  .app-header__search-input {
    border-color: rgba(255, 255, 255, 0.08);
    background: var(--bg-primary);
    color: #e0e0e0;

    &::placeholder {
      color: #777;
    }

    &:focus {
      border-color: var(--accent-primary);
      background: var(--bg-secondary);
      box-shadow: 0 0 0 4px var(--accent-primary-15);
    }
  }

  .app-header__search-icon {
    color: #777;

    .app-header__search-input:focus + & {
      color: var(--accent-primary);
    }
  }

  .app-header__search-shortcut {
    background: var(--accent-primary-10);
    border: 1px solid var(--border-accent-dark-20);
    color: var(--text-accent-dark-60);
  }

  .app-header__nav-link {
    color: #aaa;

    &:hover {
      color: var(--accent-primary);
      background: var(--accent-primary-10);
    }

    &.active {
      color: var(--accent-primary);
      background: var(--accent-primary-15);

      &::after {
        background: var(--primary-gradient);
      }
    }
  }


  .app-header__menu-toggle {
    background: linear-gradient(135deg, #2d3748 0%, #1a202c 100%);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }
  .app-header__menu-toggle span {
    background: #aaa;

    &:hover {
      background: var(--accent-primary);
    }
  }
}

// 桌面端
@media (min-width: 993px) {
  .app-header__search-toggle {
    display: none;
  }
}

// 移动端
@media (max-width: 992px) {
  .app-header {
    &__wrapper {
      padding: 0 20px;
      gap: 16px;
      height: 64px;
    }

    &__site-tagline {
      display: none;
    }

    &__logo-icon {
      width: 32px;
      height: 32px;
      border-radius: 20px;
      overflow: hidden;
    }

    &__logo-svg {
      width: 100%;
      height: 100%;
      object-fit: contain;
      border-radius: inherit;
    }

    &__site-name {
      font-size: 15px;
    }

    &__nav--desktop {
      display: none;
    }

    &__actions {
      margin-left: auto;
    }

    &__menu-toggle {
      display: flex;
    }

    &__search-toggle {
      display: flex;
    }

    &__search {
      display: none;
    }

    &__nav--mobile {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      background: var(--glass-bg-light);
      backdrop-filter: blur(32px) saturate(220%);
      -webkit-backdrop-filter: blur(32px) saturate(220%);
      border-bottom: 1px solid var(--shadow-color);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 8px;

      .app-header__nav-link {
        padding: 14px 16px;
        border-radius: 20px;

        &.active {
          background: var(--accent-primary-10);
        }
      }
    }
  }

  [data-theme="dark"] .app-header__nav--mobile {
    background: var(--glass-bg-dark);
    backdrop-filter: blur(28px) saturate(200%);
    -webkit-backdrop-filter: blur(28px) saturate(200%);
    border-bottom-color: rgba(255, 255, 255, 0.08);
  }
}

// 动画
@keyframes twinkle {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.1); }
}

.mobile-nav-enter-active,
.mobile-nav-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.mobile-nav-enter-from,
.mobile-nav-leave-to {
  opacity: 0;
  transform: translateY(-10px);
  max-height: 0;
}

.mobile-nav-enter-to,
.mobile-nav-leave-from {
  opacity: 1;
  transform: translateY(0);
  max-height: 300px;
}

// 移动端搜索按钮
.app-header__search-toggle {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 20px;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-size: 16px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  &:hover {
    color: var(--accent-primary);
    background: var(--accent-primary-06);
    transform: scale(1.05);
    box-shadow: 0 4px 16px var(--shadow-accent-20);
  }
}

[data-theme="dark"] .app-header__search-toggle {
  background: linear-gradient(135deg, #2d3748 0%, #1a202c 100%);
  color: #aaa;
  &:hover {
    color: var(--accent-primary);
    background: var(--accent-primary-10);
  }
}

// 全屏搜索面板
.app-header__search-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 12px;

  &-bar {
    width: calc(100% - 20px);
    max-width: none;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &-close {
    width: 44px;
    height: 44px;
    border: none;
    border-radius: 12px;
    background: rgba(0, 0, 0, 0.05);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #666;
    font-size: 18px;
    flex-shrink: 0;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(0, 0, 0, 0.1);
      color: #333;
    }
  }

  &-form {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &-input-wrapper {
    flex: 1;
    min-width: 0;
    position: relative;
    width: 100%;
  }

  &-input {
    width: 100%;
    height: 40px;
    padding: 0 40px 0 20px;
    border: 2px solid rgba(99, 102, 241, 0.3);
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.9);
    font-size: 16px;
    color: #333;
    outline: none;
    box-sizing: border-box;
    transition: all 0.3s ease;

    &::placeholder {
      color: #aaa;
    }

    &:focus {
      outline: none;
      box-shadow: none;
      border-color: var(--accent-primary);
    }

    &-clear {
      position: absolute;
      top: 10px;
      right: 5px;
      width: 20px;
      height: 20px;
      border: none;
      border-radius: 20px;
      background: transparent;
      color: #999;
      font-size: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      flex-shrink: 0;
      padding: 0;
      line-height: 1;
      transition: color 0.2s ease;

      &:hover {
        color: #666;
      }

      &:active {
        color: #333;
      }
    }
  }

  &-submit {
    flex-shrink: 0;
    padding: 0 16px;
    height: 40px;
    border: none;
    border-radius: 20px;
    background: rgba(0, 0, 0, 0.08);
    color: #fff;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s ease;

    &.active {
      background: var(--accent-primary);
      color: #fff;
    }

    &:active {
      background: rgba(0, 0, 0, 0.15);
    }
  }
}

[data-theme="dark"] .app-header__search-overlay {
  background: rgba(15, 15, 30, 0.9);

  .app-header__search-overlay-close {
    background: rgba(255, 255, 255, 0.08);
    color: #aaa;
    &:hover {
      background: rgba(255, 255, 255, 0.15);
      color: #e0e0e0;
    }
  }

  .app-header__search-overlay-input {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.1);
    color: #e0e0e0;

    &::placeholder {
      color: #666;
    }

    &:focus {
      border-color: var(--accent-primary);
      background: rgba(255, 255, 255, 0.1);
    }
  }
}

// 搜索面板动画
.search-panel-enter-active,
.search-panel-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.search-panel-enter-from,
.search-panel-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

.search-panel-enter-to,
.search-panel-leave-from {
  opacity: 1;
  transform: translateY(0);
}

</style>
